import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { employeesService, EmployeeContract, ContractWithUser, ContractSettings } from '@/features/employees/services/employees.service';
import { ContractTable } from '@/features/employees/components/ContractTable';
import { teamsService } from '@/features/teams/services/teams.service';
import { useToast } from '@/hooks/use-toast';
import { History, Settings } from 'lucide-react';
import { ContractEditDialog } from '@/features/employees/components/ContractEditDialog';

export const Route = createFileRoute('/_authenticated/contracts/')({
  component: ContractsManagementPage,
});

export function ContractSettingsDialogForm({
  settings,
  teams,
  saving,
  onSave,
  onCancel,
}: {
  settings: ContractSettings;
  teams: { id: string; name: string }[];
  saving: boolean;
  onSave: (data: Partial<ContractSettings>) => void;
  onCancel: () => void;
}) {
  const [notifyTeamId, setNotifyTeamId] = useState(settings.notifyTeamId || 'none');
  const [days, setDays] = useState(String(settings.daysBeforeExpiration));
  const [notifyByEmail, setNotifyByEmail] = useState(settings.notifyByEmail);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          notifyTeamId: notifyTeamId === 'none' ? null : notifyTeamId,
          daysBeforeExpiration: Number(days),
          notifyByEmail,
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Equipe para notificar</Label>
          <Select value={notifyTeamId} onValueChange={setNotifyTeamId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a equipe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhuma</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="contract-days">Dias antes do vencimento</Label>
          <Input
            id="contract-days"
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Notificar por email</div>
          <div className="text-xs text-muted-foreground">Enviar email para a equipe selecionada.</div>
        </div>
        <Switch checked={notifyByEmail} onCheckedChange={setNotifyByEmail} aria-label="Notificar por email" />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          Salvar configurações
        </Button>
      </DialogFooter>
    </form>
  );
}

function ContractsManagementPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editContractOpen, setEditContractOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<EmployeeContract | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<EmployeeContract | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-select', 'active'],
    queryFn: () => employeesService.listEmployeesForSelect({ active: true }),
  });

  const { data: contractsData = [], isLoading } = useQuery({
    queryKey: ['contracts', selectedUserId, startDate, endDate],
    queryFn: () =>
      employeesService.listContracts({
        userId: selectedUserId === 'all' ? undefined : selectedUserId,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
  });

  // Ordenar contratos pela menor quantidade de dias para vencer (considerando aditivos)
  const contracts = useMemo(() => {
    // Agrupar contratos base e aditivos (igual ao ContractTable)
    const grouped = contractsData.reduce((acc, item) => {
      const contract = item.contract;
      if (contract.contractType === 'addendum' && contract.parentContractId) {
        return acc; // Aditivos serão considerados no contrato base
      }
      acc[contract.id] = acc[contract.id] || { base: item, addendums: [] };
      acc[contract.id].base = item;
      return acc;
    }, {} as Record<string, { base: ContractWithUser; addendums: ContractWithUser[] }>);

    // Adicionar aditivos aos grupos
    contractsData.forEach((item) => {
      const contract = item.contract;
      if (contract.contractType === 'addendum' && contract.parentContractId) {
        if (grouped[contract.parentContractId]) {
          grouped[contract.parentContractId].addendums.push(item);
        }
      }
    });

    // Calcular dias até vencimento para cada grupo e ordenar
    const groupsArray = Object.values(grouped)
      .filter((group) => group.base)
      .map((group) => {
        const baseContract = group.base.contract;
        const addendumsContracts = group.addendums.map((item) => item.contract);
        const allEndDates = [
          baseContract.endDate,
          ...addendumsContracts.map((a) => a.endDate),
        ].map((date) => new Date(date));
        const maxEnd = new Date(Math.max(...allEndDates.map((date) => date.getTime())));
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        maxEnd.setHours(0, 0, 0, 0);
        const daysLeft = Math.round((maxEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return { group, daysLeft };
      })
      .sort((a, b) => a.daysLeft - b.daysLeft); // Menor quantidade de dias primeiro

    // Reconstruir array de contratos na ordem correta
    const sortedContracts: ContractWithUser[] = [];
    groupsArray.forEach(({ group }) => {
      sortedContracts.push(group.base);
      sortedContracts.push(...group.addendums);
    });

    return sortedContracts;
  }, [contractsData]);

  const { data: settings } = useQuery({
    queryKey: ['contract-settings'],
    queryFn: employeesService.getContractSettings,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsService.getAll().then((res) => res.data || []),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: employeesService.updateContractSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-settings'] });
      toast({ title: 'Configurações atualizadas' });
    },
  });

  const updateContractMutation = useMutation({
    mutationFn: ({ contractId, data }: { contractId: string; data: Parameters<typeof employeesService.updateContract>[1] }) =>
      employeesService.updateContract(contractId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast({ title: 'Contrato atualizado' });
    },
  });

  const deleteContractMutation = useMutation({
    mutationFn: (contractId: string) => employeesService.deleteContract(contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast({ title: 'Contrato excluído' });
      setDeleteDialogOpen(false);
      setContractToDelete(null);
    },
  });

  const handleDownload = async (id: string) => {
    try {
      const { url } = await employeesService.getContractDownloadUrl(id);
      window.open(url, '_blank');
    } catch {
      toast({
        title: 'Erro ao baixar contrato',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Contratos</h1>
          <p className="text-muted-foreground">
            Acompanhe contratos, aditivos e vencimentos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() =>
              navigate({
                to: '/contracts/logs',
                state: { from: '/contracts', fromLabel: 'Gestão de Contratos' },
              })
            }
          >
            <History className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-medium">Prestador</label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o prestador" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.fullName || employee.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Data início</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">Data término</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contratos cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground">Carregando contratos...</div>
          ) : (
            <ContractTable
              contracts={contracts}
              onDownload={handleDownload}
              onEdit={(contract) => {
                setSelectedContract(contract);
                setEditContractOpen(true);
              }}
                onDelete={(contract) => {
                  setContractToDelete(contract);
                  setDeleteDialogOpen(true);
                }}
              notifyDays={settings?.daysBeforeExpiration}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent size="md" busy={updateSettingsMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Configurações de contratos</DialogTitle>
            <DialogDescription>
              Defina a equipe que será notificada e o prazo de alerta.
            </DialogDescription>
          </DialogHeader>
          {settings ? (
            <ContractSettingsDialogForm
              settings={settings}
              teams={teams}
              saving={updateSettingsMutation.isPending}
              onCancel={() => setSettingsOpen(false)}
              onSave={(data) => updateSettingsMutation.mutate(data, { onSuccess: () => setSettingsOpen(false) })}
            />
          ) : (
            <div className="text-muted-foreground">Carregando configurações...</div>
          )}
        </DialogContent>
      </Dialog>

      <ContractEditDialog
        open={editContractOpen}
        onOpenChange={setEditContractOpen}
        contract={selectedContract}
        contracts={contracts.map((item) => item.contract)}
        onSubmit={async (contractId, data) => {
          await updateContractMutation.mutateAsync({ contractId, data });
        }}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent size="sm" busy={deleteContractMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Excluir contrato</DialogTitle>
            <DialogDescription>
              Tem certeza de que deseja excluir este contrato?
              Essa ação não poderá ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            {contractToDelete && (
              <>
                <p>
                  <span className="font-medium">Período:</span>{' '}
                  {new Date(contractToDelete.startDate).toLocaleDateString('pt-BR')} -{' '}
                  {new Date(contractToDelete.endDate).toLocaleDateString('pt-BR')}
                </p>
                {contractToDelete.scope && (
                  <p className="text-muted-foreground">
                    <span className="font-medium">Escopo:</span> {contractToDelete.scope}
                  </p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setContractToDelete(null);
              }}
              disabled={deleteContractMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!contractToDelete) return;
                deleteContractMutation.mutate(contractToDelete.id);
              }}
              loading={deleteContractMutation.isPending}
            >
              Excluir contrato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
